<template>
  <v-dialog
    v-model="isVisible"
    persistent
    scrollable
    max-width="1200"
  >
    <v-card>
      <v-toolbar
        flat
      >
        <v-toolbar-title>
          Short Message Template selector
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          v-if="!isLoading"
          icon
          @click="_ => closeDialog()"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <div
        v-if="isLoading"
        class="d-flex flex-column align-center justify-center"
        style="height: 350px;"
      >
        <v-progress-circular
          color="primary"
          indeterminate
          size="64"
        />
        <div
          class="pt-4 text-caption"
        >
          Loading...
        </div>
      </div>

      <v-card-text
        v-else
        class="px-0"
        style="height: 50vh;"
      >
        <v-container
          fluid
        >
          <v-row
            v-if="!shortMessageTemplates.length"
          >
            <v-col
              cols="12"
              class="align-center d-flex justify-center"
            >
              <div
                class="text-center"
              >
                <v-icon
                  size="128"
                  color="primary"
                >
                  mdi-alert-outline
                </v-icon>
                <div
                  class="grey--text mt-4 text-overline"
                >
                  There are no configured Short Message Templates to use.
                </div>
              </div>
            </v-col>
          </v-row>

          <v-row
            v-else
            class="h-100 my-0"
          >
            <v-col
              cols="12"
              md="4"
            >
              <v-list two-line>
                <v-subheader>
                  Available Templates
                </v-subheader>

                <v-text-field
                  v-model="searchTerm"
                  class="mx-4"
                  dense
                  label="Filter"
                  outlined
                />

                <v-list-item-group
                  v-if="filteredTemplates"
                  v-model="selectedTemplateId"
                >
                  <template v-for="(template, index) of filteredTemplates">
                    <v-divider
                      v-if="index > 0"
                      :key="`sep-${index}`"
                    />
                    <v-list-item
                      :key="template.id"
                      :value="template.id"
                    >
                      <template #default>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ template.title }}
                          </v-list-item-title>

                          <v-list-item-subtitle>
                            {{ template.description }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                  </template>
                </v-list-item-group>

                <v-list-item v-else>
                  There are no Checklists that match the filter
                </v-list-item>
              </v-list>
            </v-col>

            <v-divider vertical />

            <v-col
              cols="12"
              md="8"
            >
              <div style="position: sticky; top: 0px;">
                <div v-if="!selectedTemplate">
                  Please select a Template from the list.
                </div>
                <div v-else>
                  <v-card
                    v-for="translation of previewTemplateTranslations"
                    :key="translation.language"
                    class="ma-4"
                    outlined
                  >
                    <div class="px-4 text-overline">
                      <v-icon
                        left
                        size="16"
                      >
                        mdi-flag
                      </v-icon>
                      <span
                        class="font-weight-light"
                      >
                        {{ translation.language }}
                      </span>
                    </div>
                    <v-divider />
                    <div class="px-4 py-3">
                      {{ translation.example }}
                    </div>
                    <div class="px-4 pb-3 text-right">
                      <v-btn
                        depressed
                        small
                        @click="_ => setMessageText(translation.example)"
                      >
                        Use this text
                      </v-btn>
                    </div>
                  </v-card>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-btn
          text
          @click="_ => closeDialog()"
        >
          Close
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { partyName } from '@/utilities'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'
import { SHORT_MESSAGE_NAMESPACE } from '@/store/short-message'

export default {
  name: 'TextMessageEditorTemplateSelector',

  components: {
  },

  model: {
    prop: 'visible',
    event: 'change',
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      isLoading: true,
      searchTerm: '',
      selectedTemplateId: null,
    }
  },

  computed: {
    docketEntry () {
      return this.editedDocketEntry
    },

    filteredTemplates () {
      return this.shortMessageTemplates.
        filter(template => !this.searchTerm || template.title.toLowerCase().includes(this.searchTerm))
    },

    isVisible: {
      get () {
        return this.visible
      },

      set (val) {
        this.$emit('change', val)
      },
    },

    legalCase () {
      return this.legalCases?.find(lc => lc.id === this.docketEntry?.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties || []
    },

    optionsLanguages () {
      return this.getCachedDropdown('languages')
    },

    previewTemplateTranslations () {
      if (!this.selectedTemplate) return []

      const translations = this.selectedTemplate.short_message_template_translations.
        reduce((res, smtt) => {
          const language = this.optionsLanguages?.find(ol => ol.id === smtt.language_id)?.name

          res.push({
            example: this.generateConfirmationMessage(smtt.message, this.recipient),
            language: language,
            template: smtt.message,
          })

          return res
        }, [])

      return translations
    },

    recipient () {
      return this.legalCaseParties.filter(p => p.id === this.docketEntry.docket_entry_recipient.party_id)
    },

    selectedTemplate () {
      if (!this.selectedTemplateId || !this.shortMessageTemplates) return null

      return this.filteredTemplates.find(t => t.id === this.selectedTemplateId) || null
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),

    ...mapGetters(SHORT_MESSAGE_NAMESPACE, [
      'shortMessageTemplates',
    ]),
  },

  watch: {
    visible: {
      handler (value) {
        if (!value) return
        this.onVisible()
      },

      immediate: true,
    },
  },

  methods: {
    closeDialog () {
      this.isVisible = false
    },

    generateConfirmationMessage (template, party) {
      const fields = [
        {
          name: 'PartyName',
          value: partyName(party),
        },
      ]

      return fields.reduce((text, field) => text.replace(`[${field.name}]`, field.value), template)
    },

    async onVisible () {
      this.isLoading = true

      await this.getDropdownOptions({ options: false, path: 'languages' })
      await this.getShortMessageTemplates()

      this.isLoading = false
    },

    setMessageText (text) {
      this.$emit('set-message-text', text)
      this.$nextTick().then(_ => { this.closeDialog() })
    },

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),

    ...mapActions(SHORT_MESSAGE_NAMESPACE, [
      'getShortMessageTemplates',
    ]),
  },
}
</script>
